import React, { useState } from 'react';
import {
    Button,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular, Dismiss20Regular, History20Regular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { customColors, customFonts } from '../../../styles';

const useStyles = makeStyles({
    root: {
        flexShrink: 0,
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderLeft('4px', 'solid', tokens.colorBrandBackground),
        backgroundColor: tokens.colorNeutralBackground2,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
        gap: tokens.spacingHorizontalS,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    icon: {
        color: customColors.protivitiDarkBlue,
        flexShrink: 0,
    },
    label: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    toggleButton: {
        minWidth: 'unset',
        height: 'unset',
        ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
        flexShrink: 0,
    },
    body: {
        ...shorthands.padding('0', tokens.spacingHorizontalL, tokens.spacingVerticalS),
        ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
        maxHeight: '240px',
        overflowY: 'auto',
    },
    markdown: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        fontFamily: customFonts.Lato,
        lineHeight: tokens.lineHeightBase300,
        '& p': {
            ...shorthands.margin('0', '0', '6px', '0'),
        },
        '& p:last-child': {
            marginBottom: 0,
        },
        '& ul, & ol': {
            ...shorthands.margin('2px', '0', '6px', '16px'),
        },
        '& li': {
            ...shorthands.margin('1px', '0'),
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            ...shorthands.margin('10px', '0', '4px', '0'),
            fontFamily: customFonts.Lato,
            color: tokens.colorNeutralForeground1,
        },
        '& strong': {
            color: tokens.colorNeutralForeground1,
        },
        ...shorthands.padding(tokens.spacingVerticalS, '0'),
    },
});

interface MigrationContextBannerProps {
    migratedFromTitle: string;
    migrationSummary: string;
    onDismiss: () => void;
}

const MigrationContextBanner: React.FC<MigrationContextBannerProps> = ({
    migratedFromTitle,
    migrationSummary,
    onDismiss,
}) => {
    const styles = useStyles();
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={mergeClasses(styles.root)}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <History20Regular className={styles.icon} />
                    <Text className={styles.label}>
                        Migrated from{' '}
                        <span className={styles.title}>&ldquo;{migratedFromTitle}&rdquo;</span>
                    </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexShrink: 0 }}>
                    <Button
                        className={styles.toggleButton}
                        appearance="transparent"
                        size="small"
                        icon={expanded ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                        onClick={() => setExpanded((v) => !v)}
                        aria-label={expanded ? 'Hide context summary' : 'Show context summary'}
                    >
                        {expanded ? 'Hide context' : 'Show context'}
                    </Button>
                    <Button
                        className={styles.toggleButton}
                        appearance="transparent"
                        size="small"
                        icon={<Dismiss20Regular />}
                        onClick={onDismiss}
                        aria-label="Dismiss migration banner"
                        style={{ marginLeft: tokens.spacingHorizontalM }}
                    />
                </div>
            </div>
            {expanded && (
                <div className={styles.body}>
                    <div className={styles.markdown}>
                        <ReactMarkdown
                            remarkPlugins={[[remarkGfm, { tablePipeAlign: true, tableCellPadding: true }], remarkBreaks]}
                        >
                            {migrationSummary}
                        </ReactMarkdown>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MigrationContextBanner;
